import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { nameRegExp } from 'config/regex';
import { formatPinNumber, isValidPinNumber, requiresSwedishPinValidation } from 'utils/personal-identity-number';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useLazyQuery } from '@apollo/client';
import { getPatientByPINTemplate, getSamePatientOrdersTemplate } from 'apollo/templates/order';
import { Order, PatientCategory } from 'models/order';
import SamePatientOrderList from 'components/shared/same-patient-order-list/same-patient-order-list.component';
import { PatientFormData } from 'models/order-input/shared';
import isEqual from 'lodash/isEqual';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type PatientFormProps = {
    isEdit: boolean;
    patient?: PatientFormData;
    onPatientChange?: (formData: PatientFormData, isFormValid: boolean) => void;
};
type PatientNames = Pick<PatientFormData, 'firstName' | 'lastName'>;
const PatientForm: FC<PatientFormProps> = ({ isEdit, patient, onPatientChange }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [noPinChecked, setNoPinChecked] = useState(patient?.noPersonalIdentityNumber || false);
    const [samePatientOrders, setSamePatientOrders] = useState<Order[]>([]);
    const [isNewOrderVerified, setIsNewOrderVerified] = useState(true);
    const [isFormValid, setIsFormValid] = useState(false);
    // PIN whose same-patient orders were last fetched; a repeat lookup would reset the confirmation
    const lastLookedUpPinRef = useRef<string>();
    // PIN at the previous blur, or the one the form opened with; names are looked up only when it changes,
    // and an earlier lookup answering late must not fill another patient's names
    const lastEnteredPinRef = useRef<string>();

    const currentOrganization = currentOrgVar();
    const requiresSwedishPin = requiresSwedishPinValidation(currentOrganization);
    const isPatientCategoryEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PATIENT_CATEGORY,
    );
    const patientCategories = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PATIENT_CATEGORIES,
    ) as PatientCategory[];
    const gridSize = isPatientCategoryEnabled ? 3 : 4;

    const validate = (formData: PatientFormData) => {
        const errors: Record<string, string> = {};
        if (!nameRegExp.test(formData.firstName)) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!nameRegExp.test(formData.lastName)) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        // Blank names fail the backend's Name scalar (DD-3847): whitespace is not a name
        if (!formData.firstName?.trim()) {
            errors.firstName = translate('common:VALIDATION.FIELD_REQUIRED');
        }
        if (!formData.lastName?.trim()) {
            errors.lastName = translate('common:VALIDATION.FIELD_REQUIRED');
        }
        if (
            requiresSwedishPin &&
            !formData.noPersonalIdentityNumber &&
            !isValidPinNumber(formData.personalIdentityNumber)
        ) {
            errors.personalIdentityNumber = translate('common:VALIDATION.INCORRECT_PIN');
        }
        if (
            (!requiresSwedishPin || formData.noPersonalIdentityNumber) &&
            formData.personalIdentityNumber &&
            !nameRegExp.test(formData.personalIdentityNumber)
        ) {
            errors.personalIdentityNumber = translate('common:VALIDATION.CONTAINS_INVALID_CHARACTERS');
        }
        if (isPatientCategoryEnabled && !formData.patientCategory?.name) {
            errors.patientCategory = translate('common:VALIDATION.FIELD_REQUIRED');
        }
        if (samePatientOrders.length > 0 && !isNewOrderVerified) {
            errors.samePatientOrdersExists = 'SAME_PATIENT_ORDER_EXISTS';
        }

        setIsFormValid(() => Object.keys(errors).length === 0);
        return errors;
    };

    const formik = useFormik<PatientFormData>({
        initialValues: {
            firstName: patient?.firstName || '',
            lastName: patient?.lastName || '',
            personalIdentityNumber: patient?.personalIdentityNumber || '',
            noPersonalIdentityNumber: patient?.noPersonalIdentityNumber || false,
            patientCategory: patient?.patientCategory || undefined,
        },
        validate,
        onSubmit: (formData) => {
            if (onPatientChange) {
                onPatientChange(formData, true);
            }
        },
    });

    const [getSamePatientOrders] = useLazyQuery(getSamePatientOrdersTemplate, {
        onCompleted: ({ samePatientOrders }) => {
            setSamePatientOrders(samePatientOrders);
            if (samePatientOrders.length > 0) {
                setIsNewOrderVerified(false);
            }
        },
        onError: (err) => {
            showBoundary(err);
        },
    });

    // The updater form reads the values at completion time, so names typed while the lookup ran are seen.
    // The found names come from the patient's latest order, which may lack one; an empty name never overwrites.
    const applyFoundPatientNames = (foundPatient: Partial<PatientNames>) => {
        formik.setValues(
            (currentValues) => ({
                ...currentValues,
                firstName: foundPatient.firstName || currentValues.firstName,
                lastName: foundPatient.lastName || currentValues.lastName,
            }),
            true,
        );
    };

    // Without a match the names on screen are kept
    const [getPatientByPIN] = useLazyQuery(getPatientByPINTemplate, {
        fetchPolicy: 'network-only',
        onError: (err) => {
            showBoundary(err);
        },
        onCompleted: ({ patientByPIN }) => {
            if (patientByPIN && patientByPIN.personalIdentityNumber === lastEnteredPinRef.current) {
                applyFoundPatientNames(patientByPIN);
            }
        },
    });

    useEffect(() => {
        if (formik.values) {
            formik.validateForm();
        }
    }, [isNewOrderVerified, noPinChecked]);

    useEffect(() => {
        if (onPatientChange) {
            onPatientChange(formik.values, isFormValid);
        }
    }, [formik.values, isFormValid]);

    useEffect(() => {
        if (patient && !isEqual(formik.values, patient)) {
            setNoPinChecked(patient.noPersonalIdentityNumber || false);
            formik.setValues({ ...patient });
        }
    }, [patient]);

    useEffect(() => {
        // Without Swedish validation there is no checkbox — the flag mirrors whether a PIN was entered
        if (!requiresSwedishPin) {
            formik.setFieldValue('noPersonalIdentityNumber', !formik.values.personalIdentityNumber);
        }
    }, [requiresSwedishPin, formik.values.personalIdentityNumber]);

    const formatPersonalNumber = (pin: string) => (requiresSwedishPin ? formatPinNumber(pin) : pin);

    const isSamePatientLookupApplicable = (formattedPin: string) => formattedPin.length >= 6 && !isEdit;

    const lookUpSamePatientOrders = (formattedPin: string) => {
        if (!isSamePatientLookupApplicable(formattedPin)) {
            lastLookedUpPinRef.current = undefined;
            setSamePatientOrders([]);
            return;
        }
        if (formattedPin === lastLookedUpPinRef.current) {
            return;
        }
        lastLookedUpPinRef.current = formattedPin;
        getSamePatientOrders({ variables: { personalIdentityNumber: formattedPin } });
    };

    // A PIN that is already filled in when the form opens (order prefill, order form, draft) never blurs
    useEffect(() => {
        const initialPin = formatPersonalNumber(formik.values.personalIdentityNumber);
        // Captured now: the patient prop later echoes the user's edits back
        lastEnteredPinRef.current = initialPin;
        lookUpSamePatientOrders(initialPin);
    }, []);

    // With "Missing personal number" ticked the field holds a reference, not a PIN to find a patient by
    const shouldLookUpPatientNames = (isPinChanged: boolean) => isPinChanged && !formik.values.noPersonalIdentityNumber;

    const handlePersonalNumberBlur = () => {
        const formattedPin = formatPersonalNumber(formik.values.personalIdentityNumber);
        formik.setFieldValue('personalIdentityNumber', formattedPin);
        const isPinChanged = formattedPin !== lastEnteredPinRef.current;
        lastEnteredPinRef.current = formattedPin;

        // Set patient category based on PIN if enabled (For action based orders only)
        if (requiresSwedishPin && isPatientCategoryEnabled) {
            if (isValidPinNumber(formattedPin) && !patient?.noPersonalIdentityNumber) {
                patientCategoryByPin(formattedPin);
            } else if (!isValidPinNumber(formattedPin) || patient?.noPersonalIdentityNumber) {
                formik.setFieldValue('patientCategory', null, true);
            }
        }

        lookUpSamePatientOrders(formattedPin);
        if (!isSamePatientLookupApplicable(formattedPin)) {
            return;
        }

        if (!shouldLookUpPatientNames(isPinChanged)) {
            return;
        }
        getPatientByPIN({ variables: { personalIdentityNumber: formattedPin } });
    };

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        formik.setFieldValue('lastName', lastName);
    };

    const handleCheckPin = (checked: boolean) => {
        formik.setFieldValue('noPersonalIdentityNumber', checked);
        setNoPinChecked(checked);
    };

    const handlePatientCategorySelect = (selectedCategory: string) => {
        const patientCategory = patientCategories.find((cat: PatientCategory) => cat.name === selectedCategory);
        if (patientCategory) {
            patientCategory.translatedName = translate(`patient:${selectedCategory}`);
            formik.setFieldValue('patientCategory', patientCategory, true);
        }
    };

    const patientCategoryByPin = (pin: string) => {
        if (!isPatientCategoryEnabled || !patientCategories || patientCategories.length === 0) {
            return;
        }
        const presentYear = new Date().getFullYear();
        const yearFromPin = parseInt(pin.substring(0, 4));
        const age = presentYear - yearFromPin;
        const patientCategory = patientCategories.find((item) => {
            return age >= (item.minage || 0) && age <= (item.maxage || 1000);
        });
        if (patientCategory) {
            handlePatientCategorySelect(patientCategory.name);
        }
    };

    return (
        <Fragment>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={gridSize}>
                        <TextField
                            fullWidth
                            required={requiresSwedishPin && !formik.values.noPersonalIdentityNumber}
                            margin="dense"
                            label={translate('common:PERSONAL_IDENTITY_NUMBER')}
                            helperText={
                                !formik.values.noPersonalIdentityNumber &&
                                formik.touched.personalIdentityNumber &&
                                formik.errors.personalIdentityNumber
                            }
                            error={
                                !formik.values.noPersonalIdentityNumber &&
                                formik.touched.personalIdentityNumber &&
                                Boolean(formik.errors.personalIdentityNumber)
                            }
                            {...formik.getFieldProps('personalIdentityNumber')}
                            onBlur={(e) => {
                                formik.getFieldProps('personalIdentityNumber').onBlur(e);
                                handlePersonalNumberBlur();
                            }}
                            autoComplete="off"
                            data-test="patient-form.personal-id-input"
                        />
                        {requiresSwedishPin ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        onChange={(_: any, checked) => handleCheckPin(checked)}
                                        checked={noPinChecked}
                                        name="patient-form.no-pin-checkbox"
                                        data-test="patient-form.no-pin-checkbox"
                                    />
                                }
                                label={translate('orders:MISSING_PERSONAL_NUMBER')}
                            />
                        ) : undefined}
                    </Grid>
                    <Grid item xs={12} md={gridSize}>
                        <TextField
                            fullWidth
                            required
                            margin="dense"
                            label={translate('common:FIRST_NAME')}
                            helperText={formik.touched.firstName && formik.errors.firstName}
                            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                            {...formik.getFieldProps('firstName')}
                            onChange={(e) => handleFirstNameChange(e.target.value)}
                            autoComplete="off"
                            data-test="patient-form.first-name-input"
                        />
                    </Grid>
                    <Grid item xs={12} md={gridSize}>
                        <TextField
                            fullWidth
                            required
                            margin="dense"
                            label={translate('common:LAST_NAME')}
                            helperText={formik.touched.lastName && formik.errors.lastName}
                            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                            {...formik.getFieldProps('lastName')}
                            onChange={(e) => handleLastNameChange(e.target.value)}
                            autoComplete="off"
                            data-test="patient-form.last-name-input"
                        />
                    </Grid>
                    {isPatientCategoryEnabled ? (
                        <Grid item xs={12} md={gridSize}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel required>{translate('patient:PATIENT_CATEGORY')}</InputLabel>
                                <Select
                                    value={patient?.patientCategory?.name || ''}
                                    label={translate('patient:PATIENT_CATEGORY')}
                                    onChange={(event) => handlePatientCategorySelect(event.target.value)}
                                >
                                    {patientCategories.map((category: PatientCategory) => (
                                        <MenuItem key={category.name} value={category.name}>
                                            {translate(`patient:${category.name}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    ) : undefined}
                </Grid>
            </form>
            {samePatientOrders.length > 0 ? (
                <SamePatientOrderList
                    orders={samePatientOrders}
                    checked={isNewOrderVerified}
                    onChange={(checked) => setIsNewOrderVerified(checked)}
                />
            ) : undefined}
        </Fragment>
    );
};

export default PatientForm;
